import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { FieldtimeDialog } from '../../ui';
import { usePreference } from '../../../Hooks/usePreference';
import { getTodayCalendarYmd } from '../../../utils/calendarDate';
import PackageExportAPI, { saveBlob } from '../../../services/api/workflow/package-export-api';
import ExportSectionRow from './ExportSectionRow';
import ExportDateRangeSection from './ExportDateRangeSection';
import ExportPackageOptionsSection from './ExportPackageOptionsSection';
import ExportColumnsSection from './ExportColumnsSection';
import {
  FORMATS,
  boardFilterLabels,
  browserTimeZone,
  buildExportRequest,
  buildFileName,
  dateErrors,
  defaultBetweenRange,
  fileNameSuffix,
  leftOutColumns,
  sanitizeSettings,
  settingsToSave,
} from './packageExportUtils';

/**
 * PackageExportDialog — "Download package status" (#245, plan §4.5).
 *
 * Props:
 *   open, onClose
 *   onDownloaded  (fileName) => void; called after a successful download
 *   authToken
 *   projectId, projectNumber, projectName
 *   workflow      { id, name } — the board's selected workflow
 *   boardFilters  { pm: {id,name}|null, resource: {id,name}|null, vdcRequired, prefabRequired }
 */
const PackageExportDialog = ({
  open,
  onClose,
  onDownloaded,
  authToken,
  projectId,
  projectNumber,
  projectName,
  workflow,
  boardFilters,
}) => {
  const theme = useTheme();
  const { ft } = theme.palette;

  const [saved, setSaved] = usePreference(`packageExport.${projectId}`, null);
  const savedRef = useRef(saved);
  savedRef.current = saved;

  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const catalogRef = useRef(catalog);
  catalogRef.current = catalog;

  const [todayYmd, setTodayYmd] = useState(getTodayCalendarYmd);
  const [settings, setSettings] = useState(() => sanitizeSettings(saved, null));
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  // Opening (or switching project) starts from the last-used settings.
  useEffect(() => {
    if (!open) return;
    setTodayYmd(getTodayCalendarYmd());
    setSettings(sanitizeSettings(savedRef.current, catalogRef.current));
    setError(null);
  }, [open, projectId]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError(false);
    try {
      const data = await PackageExportAPI.getColumns(authToken);
      setCatalog(data);
      // Drop saved columns the catalog no longer has; fill defaults if none.
      setSettings((prev) => ({ ...prev, columns: sanitizeSettings(prev, data).columns }));
    } catch {
      setCatalogError(true);
    } finally {
      setCatalogLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    if (open && !catalog && !catalogLoading && !catalogError) loadCatalog();
  }, [open, catalog, catalogLoading, catalogError, loadCatalog]);

  const update = (patch) => {
    setError(null);
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      // Entering Between dates without dates: default to last week (Mon–Sun).
      if (patch.mode === 'between' && (!prev.startDate || !prev.endDate)) {
        Object.assign(next, defaultBetweenRange(todayYmd));
      }
      return next;
    });
  };

  const isPeriod = settings.mode === 'between';
  const columns = settings.columns || [];
  const leftOut = leftOutColumns(catalog, columns, isPeriod);
  const includedCount = columns.length - leftOut.length;
  const errors = dateErrors(settings, todayYmd);
  const hasDateErrors = Object.keys(errors).length > 0;
  const hasBoardFilters = boardFilterLabels(boardFilters).length > 0;
  const columnsLoading = catalogLoading || (!catalog && !catalogError);

  const fileName = hasDateErrors
    ? null
    : buildFileName({
        projectNumber,
        projectName,
        suffix: fileNameSuffix(settings, todayYmd),
        format: settings.format,
      });

  const canDownload =
    !downloading && !!catalog && !columnsLoading && includedCount > 0 && !hasDateErrors;

  const handleDownload = async () => {
    if (!canDownload) return;
    const effective = { ...settings, applyBoardFilters: settings.applyBoardFilters && hasBoardFilters };
    const request = buildExportRequest({
      settings: effective,
      catalog,
      workflowId: workflow?.id,
      boardFilters,
      todayYmd,
      timeZone: browserTimeZone(),
    });
    setDownloading(true);
    setError(null);
    try {
      const result = await PackageExportAPI.download(projectId, request, authToken);
      saveBlob(result.blob, result.fileName);
      setSaved(settingsToSave(settings, todayYmd));
      onDownloaded?.(result.fileName);
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = (event, reason) => {
    if (downloading) return;
    onClose?.(event, reason);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FieldtimeDialog
        open={open}
        onClose={handleClose}
        overBoard
        size="md"
        title="Download package status"
        subtitle={[projectNumber, projectName].filter(Boolean).join(' — ')}
        meta={`Workflow: ${workflow?.name || '—'} · all status lanes`}
        footerCaption={fileName}
        actions={
          <>
            <Button
              variant="text"
              onClick={handleClose}
              disabled={downloading}
              sx={{ height: 36, px: 2, fontSize: 14, color: ft.neutrals.textBody }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleDownload}
              disabled={!canDownload}
              startIcon={
                downloading ? (
                  <CircularProgress size={16} color="inherit" aria-label="Downloading" />
                ) : (
                  <FileDownloadOutlined />
                )
              }
              sx={{ height: 36, px: 2, fontSize: 14 }}
            >
              Download
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 1 }}>
          <ExportDateRangeSection
            settings={settings}
            todayYmd={todayYmd}
            errors={errors}
            onChange={update}
            disabled={downloading}
          />
          <ExportPackageOptionsSection
            settings={settings}
            boardFilters={boardFilters}
            onChange={update}
            disabled={downloading}
          />
          <ExportColumnsSection
            catalog={catalog}
            loading={columnsLoading}
            loadError={catalogError}
            onRetry={loadCatalog}
            settings={settings}
            leftOut={leftOut}
            onChange={update}
            disabled={downloading}
          />
          <ExportSectionRow label="Format" alignCenter>
            <RadioGroup
              row
              aria-label="Format"
              value={settings.format}
              onChange={(event) => update({ format: event.target.value })}
              sx={{ gap: 2.5 }}
            >
              {FORMATS.map((f) => (
                <FormControlLabel
                  key={f.value}
                  value={f.value}
                  label={f.label}
                  disabled={downloading}
                  control={<Radio size="small" sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 22 } }} />}
                  sx={{
                    m: 0,
                    gap: '10px',
                    minHeight: 32,
                    '& .MuiFormControlLabel-label': { fontSize: 14, color: ft.neutrals.textPrimary },
                  }}
                />
              ))}
            </RadioGroup>
          </ExportSectionRow>

          {error && (
            <Box
              role="alert"
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: ft.tones.error.text }}
            >
              <ErrorOutline sx={{ fontSize: 18, mt: '1px', flexShrink: 0 }} />
              <Typography component="span" sx={{ fontSize: 13, lineHeight: 1.45 }}>
                {error}
              </Typography>
            </Box>
          )}
        </Box>
      </FieldtimeDialog>
    </LocalizationProvider>
  );
};

export default PackageExportDialog;
